import { useState } from "react";
import { useHistory } from "react-router-dom";
import { displayToast } from "../../../components/app/AppToast";
import { HapticEffect } from "../../../interfaces";
import {
  gaEventJournalFollow,
  gaEventJournalUnfollow,
} from "../../../tools/analytics/domains/journal.analytics";
import { iosGenerateHaptic } from "../../../tools/ios";
import {
  useCurrentUser,
  reloadCurrentUser,
} from "../../user/hooks/useCurrentUser";
import { apiClient } from "../../../services/api";
import { useTranslation } from "react-i18next";

export type JournalFollowData = { id: string; name: string };

export function useJournalFollow(journal: JournalFollowData | null) {
  const { t } = useTranslation();
  const history = useHistory();
  const { currentUser } = useCurrentUser();
  const isFollowedStore =
    currentUser?.journals?.some((jid) => jid === journal?.id) ?? false;
  const [isFollowedOptimistic, setFollowedOptimistic] = useState<
    boolean | null
  >(null);

  const isFollowed = isFollowedOptimistic ?? isFollowedStore;

  async function toggleFollow(journal: JournalFollowData) {
    if (currentUser === null) {
      displayToast(t("✋ Please login before using this feature"), "warning");
      history.replace("/login");
      return;
    }

    const wasFollowed = isFollowed;
    const willFollow = !wasFollowed;

    setFollowedOptimistic(willFollow);
    iosGenerateHaptic(HapticEffect.SELECTION);
    displayToast(
      wasFollowed
        ? t("No longer following {{journal}}", { journal: journal.name })
        : t("You're now following {{journal}}", { journal: journal.name }),
      "info"
    );

    if (willFollow) {
      gaEventJournalFollow(journal.name);
    } else {
      gaEventJournalUnfollow(journal.name);
    }

    try {
      if (willFollow) {
        await apiClient.post("/webapp/private/users/me/journals/:journalId", {
          param: { journalId: journal.id },
        });
      } else {
        await apiClient.delete("/webapp/private/users/me/journals/:journalId", {
          param: { journalId: journal.id },
        });
      }
      reloadCurrentUser();
    } catch {
      setFollowedOptimistic(wasFollowed);
      displayToast(t("Something went wrong. Please try again."), "error");
    }
  }

  return {
    isFollowed,
    toggleFollow,
  };
}
